import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Hexagon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenido, ${user.name}`);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#020617] text-slate-100">
      <div className="hidden lg:block relative">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-[#020617]/70" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <h2 className="font-heading text-3xl font-bold tracking-tight max-w-md">
            Evaluación del talento aumentado por IA
          </h2>
          <p className="mt-3 text-slate-400 max-w-md">
            Analiza, valida y puntúa el stack de agentes de tus candidatos.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <img src="/brand/talenthackers-logo.png" alt="Talent Hackers" className="h-8 w-auto object-contain" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-primary border-l border-slate-700 pl-3">Agentic Deputy</span>
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Iniciar sesión</h1>
          <p className="text-sm text-slate-400 mt-1">Accede a tu espacio de evaluación.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 bg-slate-950 border-slate-800 focus:border-primary"
                placeholder="tu@email.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Contraseña</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 bg-slate-950 border-slate-800 focus:border-primary"
                placeholder="••••••••"
              />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full bg-primary text-slate-950 hover:bg-cyan-400">
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-400">
            ¿No tienes cuenta?{" "}
            <Link to="/register" className="text-primary hover:underline" data-testid="go-register-link">Regístrate</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
